import { Permissions } from '../models/enums/permissions';
import { uuid } from "uuidv4";
import * as utilityActions from "../actions/utility-actions";
import { IFilterBar } from "../models/utilities/filter-bar";
import { PagedApiResult } from "../models/utilities/paged-request";
import { ToastMessage } from "../models/utilities/toast-message";
import { BaseService } from "../services/_base-service";
import { ShipToAddressRead } from "../shared/models/ship-to-address-read";
import { DeliveryAddressUpdate } from "../support/models/dealers/delivery-address-update";
import { DeliveryAddressCreate, DeliveryAddressCreateResponse } from "../support/models/dealers/delivery-address-create";
import { Api, ApiResponse, StatusCode } from "../utils/api";
import { ApiEndpoints, GlobalConstants, Routes } from "../utils/app-constants";
import { AppUtils } from "../utils/app-utils";
import { navigateToPage } from "../utils/history";
import { Mapper } from "../utils/mapper";
import { OrderUtils } from '../utils/order-utils';
import { TransportationUtils } from "./helpers";
import * as Containers from './models/containers/_index';
import * as Enums from './models/enums/_index';
import { ShipmentActionType } from "./models/enums/shipment-action-type";
import { ShipmentStatus } from "./models/enums/shipment-status";
import * as Filters from './models/filters/_index';
import * as Fleet from './models/fleet/_index';
import * as PickupModels from './models/pickups/_index';
import * as Plannings from './models/planning/_index';
import { OrderSummary } from "./models/shared/order-summary";
import * as ShipmentModels from './models/shipments/_index';
import * as TruckModels from './models/trucks/_index';
import { Address } from "../models/utilities/address";
import { ShipmentActionUpdateAddress } from "../transportation/models/shipments/shipment-action-update-address"
import { hasPermission } from "../containers/authorization-helpers/has-permission";
import { OrderStatus } from "../models/ordering/enums/order-status";
import { AddressApproveStatus, DeliveryAddressApproveResponse, ShipToAddressApprove } from "./models/address/transportation-approve-address";
import { ShipmentActionAddAddon } from "./models/shipments/shipment-action-add-addon";
import { PendingAddress } from "./models/address/pending-address";

export class TransportationSvc extends BaseService {

    // #region Pickups

    /**
     * Get the details for a pickup
     * @param pickupId The pickup ID to get the details for
     */
    public getPickup = async (pickupId: number) : Promise<ApiResponse<PickupModels.PickupRead>> => {
        return await this._get(PickupModels.PickupRead, pickupId, ApiEndpoints.TRANSPORTATION_PICKUP_BY_ID);
    }

    /**
     * Create or update the provided pickup
     * @param pickup The new pickup model to create or update
     */    
    public savePickup = async (pickup: PickupModels.PickupRead) : Promise<ApiResponse<number|string>> => {

        let response;

        if (pickup.id) {

            const toUpdate              = new PickupModels.PickupUpdate();
            toUpdate.pickupTimeLocal    = pickup.pickupTime!.local;
            toUpdate.rowVersion         = pickup.rowVersion;
            toUpdate.orders = pickup.orders.reduce((acc: string[], itm: OrderSummary) => {
                acc.push(itm.id);
                return acc;
            }, []);

            // For an update, we get back the rowVersion
            const result = await this._put<string>(
                ApiEndpoints.TRANSPORTATION_PICKUP_BY_ID.formattify(pickup.id), 
                toUpdate
            );

            response = new ApiResponse<string>();
            response.status = result.status;
            response.data = result.data;
        }
        else {

            const toCreate              = new PickupModels.PickupCreate();
            toCreate.pickupTimeLocal    = pickup.pickupTime!.local;
            toCreate.dealerId           = pickup.dealerId; 
            toCreate.serviceCenterId    = pickup.serviceCenterId;            
            toCreate.orders = pickup.orders.reduce((acc: string[], itm: OrderSummary) => {
                acc.push(itm.id);
                return acc;
            }, []);

            // For a create, we get back the ID
            const result = await this._post<number>(
                ApiEndpoints.TRANSPORTATION_PICKUPS,
                toCreate
            );

            response = new ApiResponse<number>();
            response.status = result.status;
            response.data = result.data;

        }

        return response;
    }

    /**
     * Delete the provided pickup
     * @param pickup The pickup to delete
     */    
    public deletePickup = async (pickup: PickupModels.PickupRead) => {
        await this._delete(
            ApiEndpoints.TRANSPORTATION_PICKUP_BY_ID.formattify(pickup.id),
            new PickupModels.PickupDelete(pickup.rowVersion)
        );

        navigateToPage(Routes.SHIPPING_PICKUPS);
    }

    /**
     * Mark the provided pickup as delivered
     * @param pickup The pickup to mark as delivered
     */
    public markPickupAsDelivered = async (pickupId : number, rowVersion : string) : Promise<ApiResponse<number>> => {        
        return await this._post<number>(
            ApiEndpoints.TRANSPORTATION_PICKUP_MARK_DELIVERED.formattify(pickupId),
            new PickupModels.PickupDelivered(rowVersion)
        );
    }

    /**
     * Get the open orders for the provided dealer
     * @param dealerId The dealer to search for
     * @param serviceCenter The service center to use for marking wrong service center
     */    
    public getPlannedDealerOrders = async (dealerId: string, serviceCenter: string, shipmentMethod : Enums.ShipmentMethod) => {

        const response = new ApiResponse<OrderSummary[]>();
        this._showLoading();

        try {
            let url = ApiEndpoints.TRANSPORTATION_PICKUP_ORDERS_BY_DEALER.formattify(dealerId, shipmentMethod);
            
            if (serviceCenter.length > 0) {
               url += `&serviceCenterId=${ serviceCenter }`;
            }

            const orders = await Api.GetList<OrderSummary>(OrderSummary, url);
            if (orders) {

                orders.sort((itmA: OrderSummary, itmB: OrderSummary) => {
                    if (itmA.lastModified < itmB.lastModified) { return 1; }
                    if (itmA.lastModified > itmB.lastModified) { return -1; }
                    return 0;
                });

                response.status = StatusCode.Success;
                response.data = orders;
            }
        }
        catch (err) { 
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    // #endregion

    // #region Trucks

    /**
     * Get the details for a truck
     * @param truckId The truck ID to get the details for
     */
    public getTruck = async (truckId: number) : Promise<ApiResponse<TruckModels.TruckRead>> => {

        const truck = await this.getRoute(TruckModels.TruckRead, truckId, Enums.ShipmentType.Truck);
        if (truck.status === StatusCode.Success) {

            if (truck.data.driver && truck.data.driver.id && truck.data.driver.id > 0) {
                truck.data.driver.fullName = `${truck.data.driver.firstName } ${ truck.data.driver.lastName } [${ truck.data.driver.id}]`;
            }

            truck.data = TransportationUtils.buildComputedProperties(truck.data);

            const sortByIndex = (a : ShipmentModels.ShipmentAction, b : ShipmentModels.ShipmentAction) => {
                if (a.index < b.index) { return -1; }
                if (a.index > b.index) { return 1; }
                return 0;
            };
              
            truck.data.actions = truck.data.actions.sort(sortByIndex);
        }

        this._hideLoading();
        return truck;
    }

    /**
     * Get the open orders for the given ship to address 
     */
    public getOrdersForShipTo = async (orderFilter : Filters.OrderSearchFilters) : Promise<ApiResponse<PagedApiResult<OrderSummary>>> => {
        return await this.search(OrderSummary, orderFilter, true);
    }
    
    /**
     * Update the truck to the next possible status
     */
    public updateTruckStatus = async (truck : TruckModels.TruckRead) : Promise<ApiResponse<TruckModels.TruckRead>> =>  {
        return await this._updateRouteStatus<TruckModels.TruckRead>(truck);
    }

    // #endregion

    // #region Containers

    /**
     * Get a Container
     */    
    public getContainer = async (containerId : number) : Promise<ApiResponse<Containers.ContainerRead>> => {
        const container = await this.getRoute(Containers.ContainerRead, containerId, Enums.ShipmentType.Container);
        if (container.status === StatusCode.Success) {

            const sortByIndex = (a : ShipmentModels.ShipmentAction, b : ShipmentModels.ShipmentAction) => {
                if (a.index < b.index) { return -1; }
                if (a.index > b.index) { return 1; }
                return 0;
            };

            container.data.actions = container.data.actions.sort(sortByIndex);
        }

        this._hideLoading();
        return container;
    }  

    /**
     * Updates the given container to the next allowed status.
     * @param container The container to update the status of
     */
    public updateContainerStatus = async (container : Containers.ContainerRead) : Promise<ApiResponse<Containers.ContainerRead>> =>  {
        return await this._updateRouteStatus<Containers.ContainerRead>(container);
    }

    /**
     * Move a shipment from a list to the given status, on the row version the list has of it, so that a shipment that
     * was changed by someone else since is refused.  Unlike a change from its details, it shows neither a loading screen
     * nor a toast, since whoever asks says what became of each shipment.  It throws when the API refuses.
     * @param shipment The shipment as the list has it
     * @param status The status to move it to
     */
    public moveShipmentToStatus = async (shipment : ShipmentModels.ShipmentSummary, status : ShipmentStatus) : Promise<void> => {

        const url = ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTES_CHANGE_STATUS, shipment.type).formattify(shipment.id);
        const request = new ShipmentModels.ShipmentUpdateStatus(shipment.rowVersion);
        request.status = status;

        await Api.Put(url, request);
    }

    // #endregion

    // #region Planning

    /**
     * Get the unplanned routes for the provided cutoff date
     * @param routeType Decides whether to return container or truck data
     * @param filter The filter options
     */    
    public searchUnplannedRoutes = async (filter : Filters.DeliveryPlanningFilters) : Promise<ApiResponse<PagedApiResult<Plannings.UnplannedRouteGroup>>> => {
        return await this._planningSearch(Plannings.UnplannedRouteGroup, filter, true);
    }

    /**
     * Get the orders for an unplanned delivery
     * @param cutoffDate The cutoff date to get deliveries for
     */    
    public getPlannedOrders = async (cutoffDate: string | undefined, addressId: number, dealerId: string, division: string | undefined, serviceCenter : string) : Promise<ApiResponse<Plannings.PlanningDetail>> => {

        const response = new ApiResponse<Plannings.PlanningDetail>();
        this._showLoading();
        
        try {
            
            let url = ApiEndpoints.TRANSPORTATION_PLANNED_ORDERS.formattify(addressId);
            url += `&serviceCenterId=${ serviceCenter }`;
            
            if (cutoffDate) {
                url += `&maxCutoff=${ cutoffDate }`;
            }

            if (division) {
                url += `&division=${division}`;
            }

            const orders = await Api.GetList<OrderSummary>(OrderSummary, url);
            const address = await this.getAddress(dealerId, addressId.toString());

            if (orders && address) {

                const orderedList = orders.sort(OrderUtils.PlanningGroupCompare);
                
                const tmp = {
                    orders: orderedList,
                    address: address.data
                };
                
                response.data = tmp;
                response.status = 200;
            }
        }
        catch (err) { 
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    /**
     * Get the orders for an unplanned delivery
     * @param cutoffDate The cutoff date to get deliveries for
     */    
    public updateOrderGrouping = async (orders : OrderSummary[]) => {

        const response : ApiResponse<number> = new ApiResponse<number>();
        this.store.dispatch(utilityActions.showLoading());

        try {

            const vm = new Plannings.OrderGroupRequest();

            orders.forEach((o : OrderSummary) => {
                const t = new Plannings.OrderGroupUpdate();
                t.groupId       = o.groupId;
                t.id            = o.id;
                t.rowVersion    = o.rowVersion;

                vm.updates.push(t);
            });

            const result = await Api.Post(ApiEndpoints.TRANSPORTATION_PLANNING_GROUP_UPDATE, vm);
            if (result) {
                this.store.dispatch(utilityActions.hideLoading());
                response.status = 200;
            }
        }
        catch (err) { 
            console.error(err);
            const errorMsg = AppUtils.FormatError(err);
            this.store.dispatch(
                utilityActions.showToastNotification(new ToastMessage(errorMsg, "error"))
            );

            response.status = err.statusCode;
        }

        this.store.dispatch(utilityActions.hideLoading());
        return response;
    }

    /**
     * Import the .upl file and process the results
     * @param data The file object
     */    
    public importStopFile = async (data : FormData) : Promise<ApiResponse<TruckModels.TruckRead[]>> => {

        const response : ApiResponse<TruckModels.TruckRead[]> = 
            new ApiResponse<TruckModels.TruckRead[]>();

        this._showLoading();

        try {

            const result = await Api.Post(
                ApiEndpoints.TRANSPORTATION_PLANNING_IMPORT, 
                data, 
                "multipart/form-data"
            );

            if (result) {
                const mapped = Mapper.deserializeList<TruckModels.TruckRead>(TruckModels.TruckRead, result);
                if (mapped) {

                    mapped.forEach((itm : TruckModels.TruckRead) => {

                        // A temporary ID used for client identification purposes only
                        itm.tmpClientId = uuid();
                        
                        itm.actions.forEach((dItm : ShipmentModels.ShipmentAction) => {

                            itm.waypoints.push({
                                latitude    : dItm.address.latitude,
                                longitude   : dItm.address.longitude
                            });

                            dItm.tmpClientId = uuid();

                            // Origin doesn't have a distance or travel time, but for all other stop 
                            // types, we need to add their distances to the total for the Shipment as
                            // well as convert the travel time to a properly formatted span
                            if (dItm.type !== ShipmentActionType.Origin) {
                                itm.totalDistance += (dItm as ShipmentModels.TravelShipmentAction).distance;
                                (dItm as ShipmentModels.TravelShipmentAction).travelTime = AppUtils.convertTimeSpanFromServer((dItm as ShipmentModels.TravelShipmentAction).travelTime);
                                itm.totalTravelTime = AppUtils.addTimeSpansFromServer(itm.totalTravelTime, (dItm as ShipmentModels.TravelShipmentAction).travelTime);
                            }
                        });
                    });

                    response.data = mapped;
                    response.status = StatusCode.Success;
                }
            }
        }
        catch (err) { 
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    // #endregion

    // #region Dealer Addresses 

    /**
     * Fetch the address that matches the IDs passed in
     * @param dealerId The id of the dealer that the address belongs to
     * @param addressId The id of the address
     * @returns A promise of the DealerDeliveryAddress VM
     */
    public getAddress = async (dealerId : string, addressId : string) : Promise<ApiResponse<ShipToAddressRead>> => {

        const response = new ApiResponse<ShipToAddressRead>();
        this._showLoading();

        try {

            const addresses = await Api.GetList(
                ShipToAddressRead, 
                ApiEndpoints.DEALERS_TRANSPORTATION_ADDRESSES.formattify(dealerId)
            );

            if (addresses) {
                const selected = addresses.filter((itm : any) => {
                    return itm.id.toString() === addressId;
                });

                if (selected.length >= 1) {
                    response.data = selected[0];
                    response.status = StatusCode.Success;
                }
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public getPendingApprovalAddresses = async (addressId?: number | null) : Promise<ApiResponse<ShipToAddressRead[]>> => {

        const response = new ApiResponse<ShipToAddressRead[]>();
        this._showLoading();

        try {
            const endpoint = addressId
                ? ApiEndpoints.TRANSPORTATION_ADDRESSES_PENDING_APPROVAL.formattify(addressId)
                : ApiEndpoints.TRANSPORTATION_ADDRESSES_PENDING_APPROVAL.formattify('');

            const addresses = await Api.GetList<ShipToAddressRead>(
                ShipToAddressRead,
                endpoint
            );

            if (addresses) {
                response.data = addresses;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public getPendingApprovalAddressDetails = async (addressId: number): Promise<ApiResponse<PendingAddress>> => {

        const response = new ApiResponse<PendingAddress>();
        this._showLoading();

        try {
            const endpoint = ApiEndpoints.TRANSPORTATION_ADDRESSES_PENDING_APPROVAL.formattify(addressId)

            const addresses = await Api.Get<PendingAddress>(
                PendingAddress,
                endpoint
            );

            if (addresses) {
                response.data = addresses;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public approveAddress = async (id: number, addressVM : ShipToAddressApprove) : Promise<ApiResponse<DeliveryAddressApproveResponse>> => {
        return await this._post<DeliveryAddressApproveResponse>(
            ApiEndpoints.TRANSPORTATION_ADDRESSES_APPROVE.formattify(id),
            addressVM
        );
    }

    public toggleAddressReviewed = async (id: number, reviewed: boolean) => {

        var payload = {
            ShipToAddressId: id,
            reviewed: reviewed
        };

        var response = await this._put(
            ApiEndpoints.TRANSPORTATION_ADDRESSES_TOGGLE_REVIEWED.formattify(id),
            payload
        );

        if (response?.status === StatusCode.Success) {
            if (reviewed) {
                this._showToast("Address marked reviewed", "success");
            }
            else {
                this._showToast("Address marked not reviewed", "success");
            }
        }
        return response;
    }

    public saveAddressForApproval = async (id: number, addressVM: DeliveryAddressUpdate): Promise<ApiResponse<DeliveryAddressApproveResponse>> => {
        var response = await this._put<DeliveryAddressApproveResponse>(
            ApiEndpoints.TRANSPORTATION_ADDRESSES_UPDATE_FOR_APPROVAL.formattify(id),
            addressVM
        );

        if (response?.status === StatusCode.Success) {
            if (response.data?.status == AddressApproveStatus.Success) {
                this._showToast("Address saved successfully", "success");
            }
        }
        
        return response;
    }

    /**
     * Create or update the provided truck
     * @param truck The new truck model to create or update
     */
    public updateDeliveryAddress = async (id: number, addressVM: DeliveryAddressUpdate): Promise<ApiResponse<number>> => {

        const adminPermission = Permissions.SupportDealersAddressOverride;
        const isAdmin = hasPermission({ requiredPermissions: [adminPermission] });
        const endpoint = isAdmin ? ApiEndpoints.TRANSPORTATION_ADDRESSES_BY_ID_ADMIN : ApiEndpoints.TRANSPORTATION_ADDRESSES_BY_ID;
        return await this._put<number>(
            endpoint.formattify(id),
            addressVM
        );
    }

    /**
     * Create a new delivery address for the provided dealer
     * @param dealerId The dealer to add the address to
     * @param addressVM The address view model to send
     */
    public createDeliveryAddress = async (id: string, addressVM: DeliveryAddressCreate): Promise<ApiResponse<DeliveryAddressCreateResponse>> => {
        return await this._post<DeliveryAddressCreateResponse>(
            ApiEndpoints.DEALERS_TRANSPORTATION_CREATE_ADDRESS.formattify(id),
            addressVM
        );
    }

    // #endregion

    // #region Fleet - Driver Management

    /**
     * Get a driver
     */    
    public getDriver = async (driverId : number) : Promise<ApiResponse<Fleet.DriverRead>> => {

        const response : ApiResponse<Fleet.DriverRead> = new ApiResponse<Fleet.DriverRead>();
        this._showLoading();

        try {

            const url = ApiEndpoints.TRANSPORTATION_FLEET_DRIVER_BY_ID.formattify(driverId);
            const driverResponse = await Api.Get(Fleet.DriverRead, url);            
            if (driverResponse) {

                driverResponse.fullName = `${ driverResponse.firstName } ${ driverResponse.lastName }`;

                // If we got a default truck ID, we need to fetch the asset to get the 
                // asset name for display.  If that fails, we'll fallback to showing
                // the asset ID.
                if (driverResponse.defaultTruckId) {
                    const assetResponse = await this.getAsset(driverResponse.defaultTruckId);
                    if (assetResponse.status === StatusCode.Success) {
                        driverResponse.truckName = assetResponse.data.name;
                    }
                    else {
                        driverResponse.truckName = driverResponse.defaultTruckId.toString();
                    }
                }
                
                response.data = driverResponse; 
                response.status = StatusCode.Success;
            }
            
        }
        catch (err) { 
            response.status = this._handleError(err);
        }
        
        this._hideLoading();
        return response;
    }   

    /**
     * Get a driver
     */    
    public getBasicDriver = async (driverId : number) : Promise<ApiResponse<Fleet.DriverRead>> => {
        return await this._get(Fleet.DriverRead, driverId, ApiEndpoints.TRANSPORTATION_FLEET_DRIVER_BY_ID);
    }   

    /**
     * Create or update the driver provided based on the presence
     * of the ID on the object
     * @param driver The driver object that needs created/updated
     */
    public updateDriver = async (driver : Fleet.DriverRead) : Promise<ApiResponse<number>> => {

        const response = new ApiResponse<number>();
        this._showLoading();

        try {
        
            if (driver.id && driver.id > 0) {

                const toUpdate = new Fleet.DriverUpdate();
                toUpdate.employeeId = driver.employeeId;
                toUpdate.serviceCenterId  = driver.serviceCenterId;
                toUpdate.samsaraId = driver.samsaraId;
                toUpdate.firstName = driver.firstName;
                toUpdate.middleName = driver.middleName;
                toUpdate.lastName = driver.lastName;
                toUpdate.defaultTruckId = driver.defaultTruckId;
                toUpdate.rowVersion = driver.rowVersion;
                toUpdate.status = driver.status;
                toUpdate.cellPhone = driver.cellPhone.replace(/[^0-9]/g, "");
                toUpdate.alternatePhone = driver.alternatePhone.replace(/[^0-9]/g, "");
                toUpdate.passportId = driver.passportId;
                toUpdate.driversLicenseNumber = driver.driversLicenseNumber;
                toUpdate.driversLicenseState = driver.driversLicenseState;
                toUpdate.driversLicenseExpiry = driver.driversLicenseExpiry;
                toUpdate.physicalDueDate = driver.physicalDueDate;

                const result = await Api.Put(
                    ApiEndpoints.TRANSPORTATION_FLEET_DRIVER_BY_ID.formattify(driver.id),
                    toUpdate
                );

                if (result) {
                    response.status = StatusCode.Success;
                    response.data = result;
                }
            }
            else {

                const toCreate = new Fleet.DriverCreate();
                toCreate.employeeId = driver.employeeId;
                toCreate.serviceCenterId = driver.serviceCenterId;
                toCreate.samsaraId = driver.samsaraId;
                toCreate.firstName = driver.firstName;
                toCreate.middleName = driver.middleName;
                toCreate.lastName = driver.lastName;
                toCreate.defaultTruckId = driver.defaultTruckId;
                toCreate.cellPhone = driver.cellPhone.replace(/[^0-9]/g, "");
                toCreate.alternatePhone = driver.alternatePhone.replace(/[^0-9]/g, "");
                toCreate.passportId = driver.passportId;
                toCreate.driversLicenseNumber = driver.driversLicenseNumber;
                toCreate.driversLicenseState = driver.driversLicenseState;
                toCreate.driversLicenseExpiry = driver.driversLicenseExpiry;
                toCreate.physicalDueDate = driver.physicalDueDate;

                const result = await Api.Post(
                    ApiEndpoints.TRANSPORTATION_FLEET_DRIVERS, 
                    toCreate
                );

                if (result) {
                    response.status = StatusCode.Success;
                    response.data = result;
                }
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    // #endregion

    // #region Fleet - Asset Management

    /**
     * Get an asset
     */    
    public getAsset = async (assetId : number) : Promise<ApiResponse<any>> => {
        return await this._mappedGet(
            TransportationUtils.AssetTypeMapper,
            'type',
            assetId, 
            ApiEndpoints.TRANSPORTATION_FLEET_ASSET_BY_ID
        );
    }    

    /**
     * Create or update the asset provided based on the presence
     * of the ID on the object
     * @param asset The asset object that needs created/updated
     */
    public updateAsset = async (asset : Fleet.AssetRead) : Promise<ApiResponse<number>> => {

        const response = new ApiResponse<number>();
        this._showLoading();

        try {
        
            if (asset.id && asset.id > 0) {

                let toUpdate;
                if (asset.type === Enums.AssetType.Tractor) {
                    toUpdate = new Fleet.TractorUpdate();
                    toUpdate.tractorMake = (asset as Fleet.TractorRead).tractorMake;
                    toUpdate.tractorType = (asset as Fleet.TractorRead).tractorType;

                }
                else if (asset.type === Enums.AssetType.Trailer) {
                    toUpdate = new Fleet.TrailerUpdate();
                    toUpdate.trailerMake = (asset as Fleet.TrailerRead).trailerMake;
                    toUpdate.trailerType = (asset as Fleet.TrailerRead).trailerType;
                }
                else {
                    toUpdate = new Fleet.BoxTruckUpdate();
                }

                // Map the common properties
                toUpdate.status             = asset.status;
                toUpdate.name               = asset.name;
                toUpdate.year               = asset.year;
                toUpdate.axleCount          = asset.axleCount;
                toUpdate.vin                = asset.vin;
                toUpdate.licensePlateState  = asset.licensePlateState;
                toUpdate.licensePlateNumber = asset.licensePlateNumber;
                toUpdate.inServiceDate      = asset.inServiceDate;
                toUpdate.outOfServiceDate   = asset.outOfServiceDate;
                toUpdate.rowVersion         = asset.rowVersion;

                const result = await Api.Put(
                    ApiEndpoints.TRANSPORTATION_FLEET_ASSET_BY_ID.formattify(asset.id),
                    toUpdate
                );

                if (result) {
                    response.status = StatusCode.Success;
                    response.data = result;
                }   
            }
            else {

                let toCreate;
                if (asset.type === Enums.AssetType.Tractor) {
                    toCreate = new Fleet.TractorCreate();
                    toCreate.tractorMake = (asset as Fleet.TractorRead).tractorMake;
                    toCreate.tractorType = (asset as Fleet.TractorRead).tractorType;

                }
                else if (asset.type === Enums.AssetType.Trailer) {
                    toCreate = new Fleet.TrailerCreate();
                    toCreate.trailerMake = (asset as Fleet.TrailerRead).trailerMake;
                    toCreate.trailerType = (asset as Fleet.TrailerRead).trailerType;
                }
                else {
                    toCreate = new Fleet.BoxTruckCreate();
                }

                // Map the common properties
                toCreate.name               = asset.name;
                toCreate.serviceCenterId    = asset.serviceCenterId;
                toCreate.year               = asset.year;
                toCreate.axleCount          = asset.axleCount;
                toCreate.vin                = asset.vin;
                toCreate.licensePlateNumber = asset.licensePlateNumber;
                toCreate.licensePlateState  = asset.licensePlateState;
                toCreate.inServiceDate      = asset.inServiceDate;

                const result = await Api.Post(
                    ApiEndpoints.TRANSPORTATION_FLEET_ASSETS, 
                    toCreate
                );

                if (result) {
                    response.status = StatusCode.Success;
                    response.data = result;
                }   
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    // #endregion

    // #region Routes 
    
    /**
     * Get the route for the provided ID and do some basic stop
     * mapping
     * @param toClass The class to map the response to
     * @param routeId The numeric route ID to lookup 
     * @param type Whether it is a truck or a container
     */
    public getRoute = async <T extends ShipmentModels.ShipmentRead> (toClass : { new() : T },  routeId : number, type : Enums.ShipmentType)  : Promise<ApiResponse<T>> => {

        const response = new ApiResponse<T>();        
        if (routeId) {

            this._showLoading();

            try {
                
                const url = ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTE_BY_ID, type).formattify(routeId);
                let route = await Api.Get<T>(toClass, url);
                if (route) {                
                    route = this._mapStops<T>(route);
       
                    response.data = route;
                    response.status = StatusCode.Success;
                }
            }
            catch (err) {
                response.status = this._handleError(err);
                if (response.status === 404) {
                    navigateToPage(Routes.UTILITY_NOT_FOUND);
                }
            }
        }

        return response;
    }

    /**
     * Create or update the provided truck
     * @param truck The new truck model to create or update
     */
    public saveRoute = async (route : ShipmentModels.ShipmentRead, suppressToast? : boolean) : Promise<ApiResponse<number|string>> => {

        let response = new ApiResponse<number|string>();
        this._showLoading();
        
        if (route.id > 0) {
            response = await this._updateRoute(route);
        }
        else {
            response = await this._createRoute(route);

            if (response.status === StatusCode.Success && suppressToast !== true) {
                if (route.type === Enums.ShipmentType.Truck) {
                    this._showToast("Truck created successfully", "success");
                }
                else if (route.type === Enums.ShipmentType.Container) {
                    this._showToast("Container created successfully", "success");
                }            
            }
        }

        this._hideLoading();
        return response;
    }

    /**
     * Optimizes the route
     * @param route The truck to update the status of
     */
    public calculateRoute = async (stops : ShipmentModels.ShipmentAction[], optimize : boolean, type : Enums.ShipmentType) : Promise<ApiResponse<ShipmentModels.CalculateRouteResponse>> =>  {

        const response = new ApiResponse<ShipmentModels.CalculateRouteResponse>();
        this._showLoading();
        
        const vm  = new ShipmentModels.CalculateRouteRequest(optimize);
        vm.stops = stops.map((itm : ShipmentModels.ShipmentAction) => {
            return new ShipmentModels.OptimizedStopRequest(itm.index, itm.address);
        });

        try {
            const result = await Api.Post(
                ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTE_OPTIMIZE, type), 
                vm
            );

            if (result) {
                const mapped = Mapper.deserialize<ShipmentModels.CalculateRouteResponse>(ShipmentModels.CalculateRouteResponse, result) as ShipmentModels.CalculateRouteResponse;
                if (mapped) {

                    mapped.stops.forEach((itm : ShipmentModels.OptimizedStopResponse) => {
                        itm.travelTime = AppUtils.convertTimeSpanFromServer(itm.travelTime);
                    });

                    response.status = StatusCode.Success;
                    response.data = mapped;
                }
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;    
    }
    
    /**
     * Delete the provided route
     * @param truck The truck to delete
     */    
    public deleteRoute = async (routeId: number, rowVersion : string, shipmentType : Enums.ShipmentType) => {
        await this._delete(
            ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTE_BY_ID, shipmentType).formattify(routeId),
            new ShipmentModels.ShipmentDelete(rowVersion)
        );

        if (shipmentType === Enums.ShipmentType.Container) {
            navigateToPage(Routes.SHIPPING_CONTAINERS);
        }
        else {
            navigateToPage(Routes.SHIPPING_TRUCKS);
        }
    }

    public addNewStop = async (newStop : ShipmentModels.ShipmentActionCreate, truckId : number, type : Enums.ShipmentType) => {

        const response = new ApiResponse<ShipmentModels.ShipmentUpdateReponse>();
        this._showLoading();

        try {
            const result = await Api.Post(
                ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTES_NEW_STOP, type).formattify(truckId), 
                newStop
            );

            response.status = StatusCode.Success;
            response.data   = result;
        }
        catch (err) {
            response.status = this._handleError(err);
        }   

        this._hideLoading();
        return response;
    }

    public updateAddress = async (updatedAddress: Address, phoneNumber: string, truckId: number, stopId: number, truckVersion: string, type: Enums.ShipmentType, force: boolean = false): Promise<ApiResponse<DeliveryAddressApproveResponse>> => {

        let payload: any;

        payload = new ShipmentActionUpdateAddress();
        payload.addressLine1 = updatedAddress.addressLine1;
        payload.addressLine2 = updatedAddress.addressLine2;
        payload.city = updatedAddress.city;
        payload.stateProvince = updatedAddress.stateProvince;
        payload.postalCode = updatedAddress.postalCode;
        payload.country = updatedAddress.country;
        payload.phoneNumber = phoneNumber;
        payload.rowVersion = truckVersion;
        payload.latitude = updatedAddress.latitude;
        payload.longitude = updatedAddress.longitude;
        payload.timeZoneId = updatedAddress.timeZoneId;
        payload.force = force;

        var response = await this._put<DeliveryAddressApproveResponse>(
            ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTES_UPDATE_STOP_ADDRESS, type).formattify(truckId, stopId),
            payload
        );

        return response;
    }

    public updateStop = async (updatedStop : ShipmentModels.ShipmentAction, truckId : number, truckVersion : string, type : Enums.ShipmentType) => {

        let payload : any;
        if (updatedStop.type === Enums.StopType.Delivery) {

            const model = updatedStop as ShipmentModels.DeliveryShipmentAction;

            payload = new ShipmentModels.UpdateDeliveryShipmentAction();
            payload.fixedFreightAmount  = model.isFixedFreight ? model.fixedFreightAmount : undefined;
            payload.addOnCount = model.addOnCount ? model.addOnCount : undefined;
            payload.addOnWeight = model.addOnWeight ? model.addOnWeight : undefined;
            payload.note = model.note ? model.note : undefined;
            payload.rowVersion = truckVersion;

            payload.orders = model.orders.map((itm : OrderSummary) => { 
                return itm.id;
            });
        }
        else if (updatedStop.type === Enums.StopType.Backhaul) {

            const model = updatedStop as ShipmentModels.DeliveryShipmentAction;

            payload = new ShipmentModels.UpdateShipmentBackhaulActionCommand();
            payload.note = model.note ? model.note : undefined;
            payload.rowVersion = truckVersion;
        }

        return await this._put<ShipmentModels.ShipmentUpdateReponse>(
            ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTES_UPDATE_STOP, type).formattify(truckId, updatedStop.id), 
            payload
        );
    }


    public addAddon = async (orderIds: string[], truckId: number, stopId: number, truckVersion: string, type: Enums.ShipmentType) => {

        let payload = new ShipmentActionAddAddon();
        payload.shipmentId = truckId;
        payload.stopId = stopId;
        payload.rowVersion = truckVersion;
        payload.orderIds = orderIds;

        return await this._put<ShipmentModels.ShipmentUpdateReponse>(
            ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTES_ADD_ADDON, type).formattify(truckId, stopId),
            payload
        );
    }

    /**
     * Mark the provided stop as delivered
     * @param delivery The stop to mark as delivered
     * @param routeId The id of the truck the stop exists on
     * @param routeVersion The rowVersion of the parent truck
     * @returns The new rowVersion for the entity
     */
    public markStopDelivered = async (stop: ShipmentModels.ShipmentAction, routeId : number, routeVersion : string, type : Enums.ShipmentType) : Promise<ApiResponse<string>> => {
        return await this._post<string>(
            ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTES_MARK_STOP_DELIVERED, type).formattify(routeId, stop.id),
            new ShipmentModels.ShipmentActionDeliverStop(routeVersion)
        );
    }

    /**
     * Delete the provided customer delivery
     * @param stopId The customer delivery stop ID to delete
     * @param routeId The parent truck to remove the stop from
     * @param routeVersion The parent truck rowversion
     */
    public deleteStopFromRoute = async (stopId : number, routeId : number, routeVersion : string, type : Enums.ShipmentType) : Promise<ApiResponse<string>> => {
        return await this._delete(
            ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTES_UPDATE_STOP, type).formattify(routeId, stopId),
            new ShipmentModels.ShipmentDelete(routeVersion)
        );
    }

    /**
     * Update the schedule for the truck
     * @param stops A list of RouteStops that will be converted to schedule updates.
     *              NOTE: the order of the stops becomes the sequence on the server.
     * @param truckId The truck ID
     * @param truckVersion The rowVersion from the truck
     */
    public setSchedule = async (stops : ShipmentModels.ShipmentAction[], truckId : number, truckVersion : string, type : Enums.ShipmentType) => {

        const response = new ApiResponse<ShipmentModels.ShipmentUpdateReponse>();
        this._showLoading();

        const convertedList : ShipmentModels.UpdateShipmentActionSchedule[] = [];
        stops.forEach((itm : ShipmentModels.ShipmentAction) => {

            const rs = new ShipmentModels.UpdateShipmentActionSchedule();
            rs.stopId           = itm.id;
            rs.stopTimeLocal    = itm.actionTime ? itm.actionTime.local : undefined;
            
            if (itm.type !== ShipmentActionType.Origin) {
                rs.distance         = (itm as ShipmentModels.TravelShipmentAction).distance;
                rs.travelTime       = AppUtils.convertTimeSpanToServer((itm as ShipmentModels.TravelShipmentAction).travelTime);
            }

            convertedList.push(rs);

        });

        const request = new ShipmentModels.UpdateRouteScheduleRequest();
        request.rowVersion = truckVersion;
        request.entries = convertedList;

        try {
            const result = await Api.Put(
                ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTES_SET_SCHEDULE, type).formattify(truckId), 
                request
            );

            response.status = StatusCode.Success;
            response.data   = result;
        }
        catch (err) {
            response.status = this._handleError(err);
        }   

        this._hideLoading();
        return response;
    }

    /**
     * Update the schedule for the truck
     * @param stops A list of RouteStops that will be converted to schedule updates.
     *              NOTE: the order of the stops becomes the sequence on the server.
     * @param truckId The truck ID
     * @param truckVersion The rowVersion from the truck
     */
    public setCODAmounts = async (stops : ShipmentModels.DeliveryShipmentAction[], truckId : number, truckVersion : string, type : Enums.ShipmentType) => {

        const response = new ApiResponse<ShipmentModels.ShipmentUpdateReponse>();
        this._showLoading();

        // Map to a COD Amount object
        const convertedList = stops.map(( itm : ShipmentModels.DeliveryShipmentAction) => {
            return new ShipmentModels.ShipmentActionCODAmount(itm.id, itm.codAmount);
        });

        const request       = new ShipmentModels.UpdateShipmentCODRequest();
        request.rowVersion  = truckVersion;
        request.amounts     = convertedList;

        try {
            const result = await Api.Put(
                ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTES_SET_COD, type).formattify(truckId), 
                request
            );

            response.status = StatusCode.Success;
            response.data   = result;
        }
        catch (err) {
            response.status = this._handleError(err);
        }   

        this._hideLoading();
        return response;
    }

    /**
     * Update the customs info for the route
     * @param stops A list of RouteStops that will be converted to schedule updates.
     *              NOTE: the order of the stops becomes the sequence on the server.
     * @param routeId The truck ID
     * @param routeVersion The rowVersion from the truck
     */
    public setCustomsInfo = async (request : ShipmentModels.UpdateCustomsRequest, routeId : number, type : Enums.ShipmentType) => {
        return await this._put(
            ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTES_SET_CUSTOMS, type).formattify(routeId), 
            request
        );
    }

    /**
     * Queue the provided message for resending
     * @param messageId The message ID to resend
     */
    public sendNotification = async (shipmentId: number, notificationType: any) : Promise<ApiResponse<boolean>> => {

        const response = new ApiResponse<boolean>();
        this._showLoading();


        try {
            const result = await Api.Post(
                ApiEndpoints.TRANSPORTATION_ROUTE_NOTIFICATIONS_SEND,
                { shipmentId: shipmentId, notificationType: notificationType.value }
            );

            if (result) {
                response.data = result;
                response.status = StatusCode.Success;
            }
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    // #endregion

    // #region Private Methods 

    /**
     * Save updates to an existing truck
     */
    private _updateRoute = async (route : ShipmentModels.ShipmentRead) => {
        
        let payload;
        if (route.type === Enums.ShipmentType.Truck) {
            const t                = route as TruckModels.TruckRead;
            payload                = new TruckModels.TruckUpdate();
            payload.name           = t.name;
            payload.rowVersion     = t.rowVersion;
            payload.driverId       = t.driver ? t.driver.id : undefined;
            payload.tractorId      = t.tractor ? t.tractor.id : undefined;
            payload.trailerId      = t.trailer ? t.trailer.id : undefined;
            payload.cutoffDate     = t.cutoffDate ? t.cutoffDate : undefined;
            payload.note           = t.note ? t.note : undefined;
        }
        else if (route.type === Enums.ShipmentType.Container) {                
            const c                 = route as Containers.ContainerRead;
            payload                 = new Containers.ContainerUpdate();
            payload.name            = c.name;
            payload.rowVersion      = c.rowVersion;
            payload.number          = c.number;
            payload.paymentType     = c.paymentType;
            payload.size            = c.size;
            payload.cutoffDate      = c.cutoffDate ? c.cutoffDate : undefined;
            payload.carriedId       = c.carrierId;
            payload.note            = c.note ? c.note : undefined;
        }

        return await this._put<string>(
            ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTE_BY_ID, route.type).formattify(route.id),
            payload
        );
    } 

    /**
     * Updates the given route to the next allowed status.
     * @param route The truck to update the status of
     */
    private _updateRouteStatus = async <T extends ShipmentModels.ShipmentRead> (route : T) : Promise<ApiResponse<T>> =>  {

        const response = new ApiResponse<T>();
        this._showLoading();
        
        const url = ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTES_CHANGE_STATUS, route.type).formattify(route.id);
        const request = new ShipmentModels.ShipmentUpdateStatus(route.rowVersion);
        
        // Open -> Ready To Ship -> In Transit -> Closed, with In Production after Open for a route that is produced
        const nextStatus = TransportationUtils.getNextStatus(route.status, route.serviceCenterId);
        if (nextStatus === undefined) {
            this._showToast("Updating this route's status is not supported");
            response.status = StatusCode.Invalid;
            return response;
        }

        request.status = nextStatus;

        try {
            await Api.Put(
                url, 
                request
            );

            if (route.type === Enums.ShipmentType.Truck) {
                const truckResult = await this.getTruck(route.id);
                response.data = truckResult.data as unknown as T;
            }
            else {
                const containerResult = await this.getContainer(route.id);
                response.data = containerResult.data  as unknown as T;
            }

            response.status = StatusCode.Success;
            
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    /**
     * Take the route read and map to the proper create model
     */
    private _createRoute = async (route : ShipmentModels.ShipmentRead) => {

        const response = new ApiResponse<number>();

        let payload;
        if (route.type === Enums.ShipmentType.Truck) {
            const t                     = (route as TruckModels.TruckRead);
            payload                     = new TruckModels.TruckCreate();
            payload.name                = t.name;
            payload.driverId            = t.driver?.id;
            payload.tractorId           = t.tractor?.id;
            payload.trailerId           = t.trailer?.id;
            payload.serviceCenterId     = t.serviceCenterId;
            payload.cutoffDate          = t.cutoffDate ? t.cutoffDate : undefined;
            payload.note                = t.note ? t.note : undefined;

            this._buildStopCreates(payload, t);
        }
        else if (route.type === Enums.ShipmentType.Container) {
            const c                     = (route as Containers.ContainerRead);
            payload                     = new Containers.ContainerCreate();
            payload.name                = c.name;
            payload.number              = c.number;
            payload.carrierId           = c.carrierId;
            payload.size                = c.size;
            payload.paymentType         = c.paymentType;
            payload.serviceCenterId     = c.serviceCenterId;
            payload.cutoffDate          = c.cutoffDate ? c.cutoffDate : undefined;
            payload.note                = c.note ? c.note : undefined;

            this._buildStopCreates(payload, c);
        }

        try {
            const result = await Api.Post(
                ApiEndpoints.shipment(ApiEndpoints.TRANSPORTATION_ROUTES, route.type),
                payload
            );

            response.status = StatusCode.Success;
            response.data   = result;
        }
        catch (err) {
            response.status = this._handleError(err);
        }   

        return response;
    }

    /**
     * 
     * @param createModel 
     * @param readModel 
     */
    private _buildStopCreates(createModel : ShipmentModels.ShipmentCreate, readModel : ShipmentModels.ShipmentRead) {

        let tmpStop : any;
        let totalDistance = 0;
        let totalTravel = '00:00:00';

        if (readModel.actions && readModel.actions.length > 0) {                

            const origin = readModel.actions.find((s : ShipmentModels.ShipmentAction) => {
                return s.type === Enums.StopType.Origin;
            });

            if (origin) {                    
                createModel.departureTimeLocal = origin.actionTime ? origin.actionTime.local : undefined;
            }

            if (createModel.type === Enums.ShipmentType.Truck) {
                const dest = readModel.actions.find((s : ShipmentModels.ShipmentAction) => {
                    return s.type === Enums.StopType.Destination;
                });

                if (dest) {
                    totalDistance += (dest as ShipmentModels.TravelShipmentAction).distance;
                    totalTravel = AppUtils.addTimeSpansFromServer(totalTravel, (dest as ShipmentModels.TravelShipmentAction).travelTime);                    
                    (createModel as TruckModels.TruckCreate).returnTimeLocal = dest.actionTime ? dest.actionTime.local : undefined;
                }
            }

            createModel.actions = readModel.actions.filter((itm : ShipmentModels.ShipmentAction) => {
                return itm.type === Enums.StopType.Delivery;
            }).map((cd : ShipmentModels.DeliveryShipmentAction) => {
            
                totalDistance += cd.distance;
                totalTravel = AppUtils.addTimeSpansFromServer(totalTravel, cd.travelTime);

                tmpStop                 = new ShipmentModels.DeliveryShipmentActionCreate();
                tmpStop.distance        = cd.distance;
                tmpStop.stopTimeLocal   = cd.actionTime ? cd.actionTime.local : undefined;
                tmpStop.travelTime      = AppUtils.convertTimeSpanToServer(cd.travelTime);
                tmpStop.type            = cd.type;
                tmpStop.dealerId        = (cd as ShipmentModels.DeliveryShipmentAction).dealerId;
                tmpStop.shipToAddressId = (cd as ShipmentModels.DeliveryShipmentAction).shipToAddressId;

                tmpStop.orders = (cd as ShipmentModels.DeliveryShipmentAction).orders.map((oitm : OrderSummary) => {
                    return oitm.id;
                });

                return tmpStop;
            });

            if (createModel.type === Enums.ShipmentType.Truck) {
                (createModel as TruckModels.TruckCreate).totalDistance = totalDistance;
                (createModel as TruckModels.TruckCreate).totalTravelTime = AppUtils.convertTimeSpanToServer(totalTravel);
            }
        }
    }

    /**
     * Do some data cleanup on stops for the route provided.
     */
    private _mapStops = <T>(route : ShipmentModels.ShipmentRead) => {
        if (route && route.actions && route.actions.length > 0) {
            route.actions.forEach((itm : ShipmentModels.ShipmentAction) => {

                if (itm.type !== ShipmentActionType.Origin) {
                    (itm as ShipmentModels.TravelShipmentAction).travelTime = AppUtils.convertTimeSpanFromServer((itm as ShipmentModels.TravelShipmentAction).travelTime);
                    (itm as ShipmentModels.TravelShipmentAction).unloadTime = AppUtils.convertTimeSpanFromServer((itm as ShipmentModels.TravelShipmentAction).unloadTime);
                }

                if (itm.type === Enums.StopType.Delivery) {
                    const cdItm = itm as ShipmentModels.DeliveryShipmentAction;
                    (itm as ShipmentModels.DeliveryShipmentAction).isFixedFreight = (cdItm.fixedFreightAmount !== undefined && cdItm.fixedFreightAmount >= 0) ? true : false;
                }
            });
        }

        return route as unknown as T;
    }

    /**
     * Do a planning search based on the provided filters
     * @param toClass The type to deserialize the response data as
     * @param filter The filter bar object holding the search params
     * @param checkAddresses A flag for whether or not to check if 
     *                       addresses of response are bad
     */    
    private _planningSearch = async <T> (toClass : { new() : T }, filter : IFilterBar, checkAddresses : boolean) : Promise<ApiResponse<PagedApiResult<T>>> => {

        const response = new ApiResponse<PagedApiResult<T>>();
        this._showLoading();

        try {

            const result = await Api.GetList<T>(
                toClass,
                AppUtils.convertFiltersToUrl(filter.baseUrl, filter)
            );
            
            if (result) {

                if (checkAddresses) {
                    // Do our bad address checking here
                    // Only used for routes
                    result.forEach((itm : any) => {
                        itm.isBadAddress = !TransportationUtils.isValidAddress(itm.deliveryAddress);
                        itm.tmpClientId = uuid();
                    });
                }

                const pagedresult = new PagedApiResult<T>();
                pagedresult.results = result;
                
                response.data = pagedresult;
                response.status = StatusCode.Success;

            }
        }
        catch (err) { 
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }    

    //#endregion

}
